﻿const personSection = () => {
  // CSS classes for modal and overlay states
  const modalClosedClasses = [
      "max-h-0",
      "opacity-0",
      "overflow-hidden",
      "pointer-events-none",
      "translate-y-[60vh]",
    ],
    modalOpenClasses = [
      "max-h-[90vh]",
      "opacity-1",
      "overflow-auto",
      "pointer-events-auto",
      "-translate-y-1/2",
    ],
    overlayClosedClasses = ["opacity-0", "pointer-events-none"],
    overlayOpenClasses = ["opacity-30"];

  // Data attribute selectors
  const itemSelector = "[data-person-section-item]",
    modalSelector = "[data-person-section-item-modal]",
    openTriggerSelector = "[data-person-section-item-trigger-open]",
    closeTriggerSelector = "[data-person-section-item-trigger-close]",
    overlaySelector = "[data-overlay]";

  // Get all person items and overlay element
  const items = document.querySelectorAll<HTMLElement>(itemSelector),
    overlay = document.querySelector<HTMLElement>(overlaySelector);

  items.forEach(item => {
    const triggerOpen = item.querySelector<HTMLElement>(openTriggerSelector),
      triggerClose = item.querySelector<HTMLElement>(closeTriggerSelector),
      modal = item.querySelector<HTMLElement>(modalSelector);

    if (!triggerOpen || !triggerClose || !modal) {
      return;
    }

    // Toggle modal on open button click
    triggerOpen!.onclick = event => {
      event.preventDefault();
      event.stopPropagation();

      closeModals();

      if (!modalOpenClasses.every(openClass => modal!.classList.contains(openClass))) {
        openModal(item);
      }
    };

    // Close modal on close button click
    triggerClose!.onclick = event => {
      event.preventDefault();
      event.stopPropagation();

      closeModal(item);
    };
  });

  // Close modals on outside modal click
  document.onclick = event => {
    const openModals = Array.from(items).filter(item => {
      const modal = item.querySelector(modalSelector);
      return (
        modal !== null &&
        modalOpenClasses.every(openClass => modal.classList.contains(openClass))
      );
    });

    if (!openModals.some(modal => modal.contains(event.target as Node))) {
      closeModals();
    }
  };

  // Close modals on Escape key press
  document.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Escape" || event.key === "Esc") {
      closeModals();
    }
  });

  // Open a specific modal
  function openModal(item: HTMLElement) {
    const modal = item.querySelector<HTMLElement>(modalSelector);

    if (modal) {
      openOverlay(modal);

      modal.inert = false;
      modal.classList.remove(...modalClosedClasses);
      modal.classList.add(...modalOpenClasses);
    }

    const openTrigger = item.querySelector<HTMLElement>(openTriggerSelector);

    if (openTrigger) {
      openTrigger.setAttribute("aria-expanded", "true");
    }

    const closeTrigger = item.querySelector<HTMLElement>(closeTriggerSelector);

    if (closeTrigger) {
      closeTrigger.setAttribute("tabindex", "0");
    }
  }

  // Close all modals
  function closeModals() {
    items.forEach(item => {
      closeModal(item);
    });
  }

  // Close a specific modal
  function closeModal(item: HTMLElement) {
    closeOverlay();

    const modal = item.querySelector<HTMLElement>(modalSelector);

    if (modal) {
      modal.inert = true;
      modal.classList.remove(...modalOpenClasses);
      modal.classList.add(...modalClosedClasses);
    }

    const openTrigger = item.querySelector<HTMLElement>(openTriggerSelector);

    if (openTrigger) {
      openTrigger.setAttribute("aria-expanded", "false");
    }

    const closeTrigger = item.querySelector<HTMLElement>(closeTriggerSelector);

    if (closeTrigger) {
      closeTrigger.setAttribute("tabindex", "-1");
    }
  }

  // Open overlay when any modal is open
  function openOverlay(modal: HTMLElement) {
    if (overlay) {
      overlay.classList.remove(...overlayClosedClasses);
      overlay.classList.add(...overlayOpenClasses);

      disableScrollAllowModal(modal);
    }
  }

  // Close overlay when no modals are open
  function closeOverlay() {
    if (overlay) {
      overlay.classList.remove(...overlayOpenClasses);
      overlay.classList.add(...overlayClosedClasses);

      enableScroll();
    }
  }

  // Temporary variables for scroll locking
  let locked = false;
  let lockY = 0;
  let rootAllowed: HTMLElement | null = null;

  let activeScrollable: HTMLElement | null = null;
  let touchStartY = 0;

  const SCROLL_KEYS = new Set([
    "ArrowUp",
    "ArrowDown",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    " ", // Space
  ]);

  // Check if the element has scrollable overflow
  function isOverflowScrollable(element: HTMLElement): boolean {
    const style = getComputedStyle(element);

    return style.overflowY !== "visible" && style.overflowY !== "hidden";
  }

  // Find the nearest scrollable ancestor within the allowed root
  function findScrollableAncestor(
    target: Element,
    within: HTMLElement
  ): HTMLElement | null {
    let element: Element | null = target;
    while (element && within.contains(element)) {
      if (element instanceof HTMLElement) {
        if (
          element.scrollHeight > element.clientHeight &&
          isOverflowScrollable(element)
        ) {
          return element;
        }
      }
      element = element.parentElement;
    }

    return null;
  }

  // Determine if the element can consume the scroll event
  function willConsume(element: HTMLElement, deltaY: number): boolean {
    if (deltaY === 0) {
      return false;
    }

    const top = element.scrollTop;
    const max = element.scrollHeight - element.clientHeight;

    if (deltaY < 0) {
      // scrolling up
      return top > 0;
    } else {
      // scrolling down
      return top < max - 0.5; // sub-pixel tolerance
    }
  }

  // Event handlers for scroll locking
  function wheelHandler(event: WheelEvent) {
    if (!rootAllowed) {
      event.preventDefault();
      return;
    }
    const target = event.target as Element;

    const element = findScrollableAncestor(target, rootAllowed);
    if (!element) {
      event.preventDefault();
      return;
    }

    if (willConsume(element, event.deltaY)) {
      // allow inside modal
      return;
    }

    event.preventDefault();
  }

  // Touch handling for mobile devices
  function touchStartHandler(event: TouchEvent) {
    if (!rootAllowed) {
      return;
    }

    const touch = event.touches[0];
    touchStartY = touch.clientY;
    const target = event.target as Element;
    activeScrollable = findScrollableAncestor(target, rootAllowed);
  }

  // Touch move handler to manage scroll locking
  function touchMoveHandler(event: TouchEvent) {
    if (!rootAllowed) {
      event.preventDefault();
      return;
    }
    if (!activeScrollable) {
      event.preventDefault();
      return;
    }

    const touch = event.touches[0];
    const deltaY = touchStartY - touch.clientY; // positive = scrolling down

    if (willConsume(activeScrollable, deltaY)) {
      touchStartY = touch.clientY; // reset baseline
      return; // allow modal scroll
    }

    event.preventDefault(); // block when at edges
  }

  // Keydown handler for keyboard navigation
  function keydownHandler(event: KeyboardEvent) {
    if (!rootAllowed) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable)
    ) {
      return;
    }

    if (!SCROLL_KEYS.has(event.key)) {
      return;
    }

    const focus = (document.activeElement as Element) || rootAllowed;
    const element = findScrollableAncestor(focus, rootAllowed) || rootAllowed;

    let deltaY = 0;
    const line = 40;

    switch (event.key) {
      case "ArrowDown":
        deltaY = line;
        break;
      case "ArrowUp":
        deltaY = -line;
        break;
      case "PageDown":
        deltaY = element.clientHeight * 0.9;
        break;
      case "PageUp":
        deltaY = -element.clientHeight * 0.9;
        break;
      case "End":
        deltaY = Number.POSITIVE_INFINITY;
        break;
      case "Home":
        deltaY = Number.NEGATIVE_INFINITY;
        break;
      case " ":
        deltaY = event.shiftKey
          ? -element.clientHeight * 0.9
          : element.clientHeight * 0.9;
        break;
    }

    if (willConsume(element, deltaY > 0 ? 1 : -1)) {
      // manually scroll to avoid body jump at edges
      if (deltaY === Number.POSITIVE_INFINITY) {
        element.scrollTop = element.scrollHeight;
      } else if (deltaY === Number.NEGATIVE_INFINITY) {
        element.scrollTop = 0;
      } else {
        element.scrollBy({ top: deltaY, behavior: "auto" });
      }
      event.preventDefault();
    } else {
      event.preventDefault();
    }
  }

  // Prevent window scroll when locked
  function windowScrollSnapback() {
    if (window.scrollY !== lockY) {
      window.scrollTo(0, lockY);
    }
  }

  // Disable background scroll but allow within the modal
  function disableScrollAllowModal(modalElement: HTMLElement) {
    if (locked) {
      return;
    }

    locked = true;
    rootAllowed = modalElement;

    lockY = window.scrollY;

    document.addEventListener("wheel", wheelHandler, { passive: false });
    document.addEventListener("touchstart", touchStartHandler, { passive: true });
    document.addEventListener("touchmove", touchMoveHandler, { passive: false });
    document.addEventListener("keydown", keydownHandler, { passive: false });

    window.addEventListener("scroll", windowScrollSnapback, { passive: true });

    document.documentElement.style.overscrollBehavior = "none";
    rootAllowed.style.overscrollBehavior = "contain";
  }

  // Re-enable normal scrolling behavior
  function enableScroll() {
    if (!locked) {
      return;
    }

    locked = false;

    document.removeEventListener("wheel", wheelHandler);
    document.removeEventListener("touchstart", touchStartHandler);
    document.removeEventListener("touchmove", touchMoveHandler);
    document.removeEventListener("keydown", keydownHandler);
    window.removeEventListener("scroll", windowScrollSnapback);

    document.documentElement.style.overscrollBehavior = "";
    if (rootAllowed) {
      rootAllowed.style.overscrollBehavior = "";
    }

    rootAllowed = null;
    activeScrollable = null;
  }
};

personSection();
